import { holidays } from "../data/holidaysList";
import {
  IStrategy,
  IGenerateStrategyProps,
  IStrikeRange,
  IStrikeData,
  ViewType,
} from "../types";
import {
  getDurationToExpiry,
  getAtmCallStrikeIndex,
  getAtmPutStrikeIndex,
  getCallRange,
  getPutRange,
  getBearishCallRange,
  getBullishPutRange,
} from "./strategyUtils";
import { generateBearCallSpread } from "./strategies/common/generateBearCallSpread";
import { generateBullCallSpread } from "./strategies/common/generateBullCallSpread";
import { generateBullPutSpread } from "./strategies/common/generateBullPutSpread";
import { generateBearPutSpread } from "./strategies/common/generateBearPutSpread";
import { generateShortStraddle } from "./strategies/neutral/generateShortStraddle";
import { generateLongStraddle } from "./strategies/extremely bullish/generateLongStraddle";
import { generateLongStrangle } from "./strategies/extremely bullish/generateLongStrangle";
import { generateBearPutLadder } from "./strategies/ladder/generateBearPutLadder";
import { generateShortStrangle } from "./strategies/neutral/generateShortStrangle";
import { generateBullCallLadder } from "./strategies/ladder/generateBullCallLadder";
import { generateLowPremiumCalls } from "./strategies/common/generateLowPremiumCalls";
import { generateLowPremiumPuts } from "./strategies/common/generateLowPremiumPuts";

export const generateStrategies = (
  params: IGenerateStrategyProps
): IStrategy[] => {
  const { strikePrices, underlyingValue, data } = params.data;
  const { expiryDate, maxStrikes } = params;
  const strategies: IStrategy[] = [];
  const atmCallIndex = getAtmCallStrikeIndex(strikePrices, underlyingValue);
  const atmPutIndex = getAtmPutStrikeIndex(strikePrices, underlyingValue);
  const expiryDuration = getDurationToExpiry(expiryDate, holidays);

  const callRange = getCallRange(
    strikePrices,
    atmCallIndex,
    maxStrikes,
    expiryDuration
  );
  const putRange = getPutRange(
    strikePrices,
    atmPutIndex,
    maxStrikes,
    expiryDuration
  );
  const bearishCallRange = getBearishCallRange(
    strikePrices,
    atmCallIndex,
    maxStrikes,
    expiryDuration
  );
  const bullishPutRange = getBullishPutRange(
    strikePrices,
    atmPutIndex,
    maxStrikes,
    expiryDuration
  );

  const getFilteredCallData = (
    strikeData: IStrikeData[],
    range: IStrikeRange,
    expiryDate: string
  ): IStrikeData[] => {
    return strikeData.filter(
      (value) =>
        value.expiryDates === expiryDate &&
        value.strikePrice >= range.startRange &&
        value.strikePrice <= range.endRange
    );
  };
  const getFilteredPutData = (
    strikeData: IStrikeData[],
    range: IStrikeRange,
    expiryDate: string
  ): IStrikeData[] => {
    return strikeData.filter(
      (value) =>
        value.expiryDates === expiryDate &&
        value.strikePrice <= range.startRange &&
        value.strikePrice >= range.endRange
    );
  };
  const filteredCallStrikes = getFilteredCallData(data, callRange, expiryDate);
  const filteredPutStrikes = getFilteredPutData(data, putRange, expiryDate);
  const filteredBearishCallStrikes = getFilteredCallData(
    data,
    bearishCallRange,
    expiryDate
  );
  const filteredBullishPutStrikes = getFilteredPutData(
    data,
    bullishPutRange,
    expiryDate
  );

  const viewType = params.viewType;

  if(viewType === ViewType["Extremely Positive"]) {
    generateBullCallLadder(
      params,
      filteredCallStrikes,
      strategies,
      expiryDuration
    )
  }
  if(viewType === ViewType["Extremely Negative"]) {
    generateBearPutLadder(
      params,
      filteredPutStrikes,
      strategies,
      expiryDuration
    )
  }
  if (
    viewType === ViewType.Up ||
    viewType === ViewType["Extremely Positive"] ||
    viewType === ViewType["Moderately Positive"]
  ) {
    generateLowPremiumCalls(
      params,
      filteredCallStrikes,
      strategies,
      expiryDuration
    );
    generateBullCallSpread(
      params,
      filteredCallStrikes,
      strategies,
      expiryDuration
    );
    generateBullPutSpread(
      params,
      filteredBullishPutStrikes,
      strategies,
      expiryDuration
    );
  }
  if (
    viewType === ViewType.Down ||
    viewType === ViewType["Moderately Negative"] ||
    viewType === ViewType["Extremely Negative"]
  ) {
    generateLowPremiumPuts(
      params,
      filteredCallStrikes,
      strategies,
      expiryDuration
    );
    generateBearCallSpread(
      params,
      filteredBearishCallStrikes,
      strategies,
      expiryDuration
    );
    generateBearPutSpread(
      params,
      filteredPutStrikes,
      strategies,
      expiryDuration
    );
  }

  if (
    viewType === ViewType.Neutral ||
    viewType === ViewType["Moderately Positive"] ||
    viewType === ViewType["Moderately Negative"]
  ) {
    generateShortStraddle(
      params,
      strategies,
      atmCallIndex,
      atmPutIndex,
      expiryDuration
    );
    generateShortStrangle(
      params,
      strategies,
      atmCallIndex,
      atmPutIndex,
      expiryDuration
    );
  }

  if (
    viewType === ViewType["Extremely Negative"] ||
    viewType === ViewType["Extremely Positive"]
  ) {
    generateLongStraddle(
      //Long straddle
      params,
      strategies,
      atmCallIndex,
      atmPutIndex,
      expiryDuration
    );
    generateLongStrangle(
      //Long strangle
      params,
      strategies,
      atmCallIndex,
      atmPutIndex,
      expiryDuration
    );
  }
  
  return strategies;
};
